import { useTranslation } from '@silospot/i18n/react';
import type { DimensionRow } from '@silospot/shared';
import { useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardHead, EstimatedNote, PageHeader } from '../../components/cld';
import { BarsH } from '../../components/cld/charts';
import { Flag } from '../../components/location';
import { api } from '../../lib/api';
import { usePeriodRange } from '../../stores/period-store';

export function AudiencePage(): ReactElement {
  const { t } = useTranslation('pages');
  const { t: ts } = useTranslation('shell');
  const { id = '' } = useParams();
  const range = usePeriodRange();
  const { data, isLoading } = useQuery({
    queryKey: ['cld-audience', id, range],
    queryFn: () => api.getAudience(id, range),
    enabled: !!id,
  });

  const nf = new Intl.NumberFormat('fr-FR');
  const header = <PageHeader title={ts('nav.audience')} sub={t('audience.fraudRate')} />;
  if (isLoading || !data) {
    return (
      <div>
        {header}
        <Card>
          <div className="h-64 animate-pulse rounded-lg bg-neutral-bg" />
        </Card>
      </div>
    );
  }

  const dims: { key: string; rows: DimensionRow[]; flags?: boolean }[] = [
    { key: 'countries', rows: data.countries, flags: true },
    { key: 'devices', rows: data.devices },
    { key: 'browsers', rows: data.browsers },
    { key: 'os', rows: data.os },
  ];

  return (
    <div>
      {header}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {dims.map((d) => (
          <Card key={d.key}>
            <CardHead title={t(`audience.${d.key}`)} />
            <BarsH
              data={d.rows.map((r) => ({
                label: r.key || '—',
                labelNode: d.flags ? (
                  <>
                    <Flag country={r.key} />
                    {r.key || '—'}
                  </>
                ) : undefined,
                value: r.visits,
                risk: r.visits > 0 ? (r.suspect / r.visits) * 100 : 0,
              }))}
              valueFmt={(v) => nf.format(v)}
            />
            {d.rows.length === 0 ? (
              <p className="py-4 text-center text-[13px] text-text-3">—</p>
            ) : null}
          </Card>
        ))}
      </div>
      <div className="mt-4">
        <EstimatedNote />
      </div>
    </div>
  );
}
